import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "../../lib/AuthContext";
import { useTenants, useCreateTenant, useFeatureFlags } from "../../lib/api";
import { visibleEscalationTabs } from "../../lib/escalationTabs";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import StageEditor from "./StageEditor";
import BrandLogoEditor from "./BrandLogoEditor";
import BrandSidebarImageEditor from "./BrandSidebarImageEditor";
import Chat360Editor from "./Chat360Editor";
import Chat360FallbackEditor from "./Chat360FallbackEditor";
import People from "./People";
import ComplaintConfigEditor from "./ComplaintConfigEditor";
import FeatureFlagsEditor from "./FeatureFlagsEditor";
import NotificationsEditor from "./NotificationsEditor";
import CostEditor from "./CostEditor";
import SystemHealth from "./SystemHealth";
import SlaEditor from "./SlaEditor";
import PreIfrWorkshop from "@packs/automotive/web/pages/SuperAdmin/PreIfrWorkshop";
import { useFirstMessage } from "@packs/automotive/web/pages/SuperAdmin/PreIfrFirstMessage";
import TataIntegration from "./TataIntegration";
import Vehicles from "@packs/automotive/web/pages/SuperAdmin/Vehicles";
import Templates from "./Templates";
import { ICONS, Page, PageHeader, cardClass, initials } from "./primitives";
import {
  AccountMenu,
  HoverSidebar,
  MENU_CLASS,
  usePopover,
} from "../../components/AppShell";
import OrganizationPage from "../msd/org/OrganizationPage";
import RolesPage from "../msd/org/RolesPage";
import "./superadmin-theme.css";

// Super Admin may see and change every part of a brand's organization.
const FULL_ACCESS = Object.fromEntries(
  ["regions", "dealers", "workshops", "people"].map((k) => [
    k,
    { see: true, edit: true },
  ]),
);

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const slugify = (v) =>
  v
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

const NAV_GROUPS = [
  {
    label: "Customer experience",
    accent: "#E8590C",
    items: [
      { key: "journey", label: "Service journey", icon: "route" },
      { key: "escalations", label: "Escalations", icon: "bell" },
      { key: "templates", label: "Templates", icon: "message" },
    ],
  },
  {
    label: "Organization",
    accent: "#2F9E44",
    items: [
      { key: "organization", label: "Regions & workshops", icon: "map" },
      { key: "people", label: "People", icon: "users" },
      { key: "roles", label: "Roles & access", icon: "shield" },
      { key: "vehicles", label: "Vehicles", icon: "car" },
    ],
  },
  {
    label: "Platform",
    accent: "#2667B5",
    items: [{ key: "health", label: "System health", icon: "pulse" }],
  },
  {
    label: "Settings",
    accent: "#667085",
    items: [
      { key: "cost", label: "Cost", icon: "rupee" },
      { key: "features", label: "Feature access", icon: "sliders" },
      { key: "notifications", label: "Notifications", icon: "bell" },
      { key: "brands", label: "Brands", icon: "layers" },
    ],
  },
];

const PAGE_LOOKUP = Object.fromEntries(
  NAV_GROUPS.flatMap((g) =>
    g.items.map((it) => [it.key, { ...it, group: g.label }]),
  ),
);

const ESCALATION_TABS = [
  { key: "response", label: "Reply Pending" },
  { key: "join", label: "Pending Invitations" },
  { key: "sentiment", label: "Customer Complaints / Dissatisfaction" },
  { key: "predelivery_feedback", label: "Negative Pre Delivery Feedback" },
  { key: "postdelivery_feedback", label: "Negative Post Delivery Feedback" },
  { key: "staff_conduct", label: "Negative Staff Conduct" },
  { key: "complaint", label: "New Complaint Detected" },
  // Opt-in per brand (Feature access -> Journeys): off for everyone until switched on.
  {
    key: "pre_ifr_unhappy",
    label: "Pre-IFR Journey: Unhappy",
    requiresFlag: "pre_ifr_journey",
  },
  { key: "complaints", label: "Complaint Tracking" },
];

function NewTenantForm({ onDone }) {
  const create = useCreateTenant();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [masterEmail, setMasterEmail] = useState("");

  const effectiveSlug = slugTouched ? slug : slugify(name);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !effectiveSlug) {
      toast.error("Brand name and slug are both required.");
      return;
    }
    if (!EMAIL_RE.test(masterEmail.trim())) {
      toast.error("Enter a valid Master email for this brand.");
      return;
    }
    try {
      await create.mutateAsync({
        name: name.trim(),
        brand_slug: effectiveSlug,
        master_email: masterEmail.trim().toLowerCase(),
      });
      toast.success(
        "Brand created. The Master can now sign up with that email.",
      );
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed.");
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <label className="mb-1.5 block text-xs font-semibold text-[hsl(var(--ink-2))]">
          Brand name
        </label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Acme Motors"
        />
      </div>
      <div>
        <label className="mb-1.5 block text-xs font-semibold text-[hsl(var(--ink-2))]">
          Slug
        </label>
        <Input
          value={effectiveSlug}
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(slugify(e.target.value));
          }}
          placeholder="acme-motors"
        />
      </div>
      <div>
        <label className="mb-1.5 block text-xs font-semibold text-[hsl(var(--ink-2))]">
          Master email
        </label>
        <Input
          type="email"
          value={masterEmail}
          onChange={(e) => setMasterEmail(e.target.value)}
          placeholder="owner@acmemotors.com"
        />
        <p className="mt-1.5 text-xs text-muted-foreground">
          This login is bound to this brand. They sign up with this exact
          address and see only this brand's workshops.
        </p>
      </div>
      <Button type="submit" disabled={create.isPending} className="w-full">
        {create.isPending && <Loader2 className="animate-spin" size={14} />}
        Create brand
      </Button>
    </form>
  );
}

function UpDown() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#9CA3AF"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="flex-none"
    >
      <path d="M8 9l4-4 4 4M8 15l4 4 4-4" />
    </svg>
  );
}

function BrandSwitcher({ tenants, selected, onSelect, onAddBrand, loading }) {
  const { open, setOpen, ref } = usePopover();

  const name = selected
    ? selected.name
    : loading
      ? "Loading"
      : "Select a brand";

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex h-12 min-w-0 cursor-pointer items-center gap-3 rounded-xl border border-[#EAECF0] bg-white py-1.5 pl-1.5 pr-3 text-left shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-[border-color,box-shadow] duration-150 hover:border-[#D0D5DD] hover:shadow-[0_2px_6px_rgba(16,24,40,0.06)]"
      >
        <span className="flex h-10 w-10 flex-none items-center justify-center overflow-hidden rounded-xl bg-white text-[13px] font-semibold text-[#4B5563] shadow-[0_0_0_1px_#E5E7EB]">
          {selected?.logo_url ? (
            <img
              src={selected.logo_url}
              alt=""
              className="h-full w-full object-contain p-0.5"
            />
          ) : (
            initials(selected?.name) || "–"
          )}
        </span>
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-[16px] font-semibold leading-5 text-[#111827]">
            {name}
          </span>
          {selected?.master_email && (
            <span className="truncate text-[12px] leading-4 text-[#6B7280]">
              {selected.master_email}
            </span>
          )}
        </span>
        <UpDown />
      </button>
      {open && (
        <div className={`${MENU_CLASS} left-0 w-[288px]`} style={{ top: 56 }}>
          <div className="px-[10px] pb-[6px] pt-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#6B7280]">
            Brands
          </div>
          {tenants.map((t) => {
            const current = t.id === selected?.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  onSelect(t.id);
                  setOpen(false);
                }}
                className="flex w-full cursor-pointer items-center gap-[10px] rounded-lg border border-transparent px-[10px] py-2 text-left transition-colors duration-150 hover:bg-[#F4F6F9]"
                style={{ background: current ? "#EFF5FC" : undefined }}
              >
                <span className="flex h-8 w-8 flex-[0_0_32px] items-center justify-center overflow-hidden rounded-lg bg-white text-[11px] font-semibold text-[#4B5563] shadow-[0_0_0_1px_#E5E7EB]">
                  {t.logo_url ? (
                    <img
                      src={t.logo_url}
                      alt=""
                      className="h-full w-full object-contain p-0.5"
                    />
                  ) : (
                    initials(t.name)
                  )}
                </span>
                <span className="flex min-w-0 flex-1 flex-col gap-px">
                  <span className="text-[13px] font-medium text-[#111827]">
                    {t.name}
                  </span>
                  <span className="truncate text-[11px] text-[#6B7280]">
                    {t.master_email || ""}
                  </span>
                </span>
                {current && (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#2667B5"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                )}
              </button>
            );
          })}
          <div className="mx-1 my-[6px] h-px bg-[#F0F1F3]" />
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onAddBrand();
            }}
            className="flex w-full cursor-pointer items-center gap-2 rounded-lg border border-transparent bg-transparent px-[10px] py-[9px] text-[13px] font-medium text-[#2667B5] transition-colors duration-150 hover:bg-[#EFF5FC]"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
            Add brand
          </button>
        </div>
      )}
    </div>
  );
}

function EscalationsPage({ tenantId }) {
  const { data: flags } = useFeatureFlags(tenantId);
  const tabs = useMemo(
    () => visibleEscalationTabs(ESCALATION_TABS, flags),
    [flags],
  );
  const [tabKey, setTabKey] = useState(ESCALATION_TABS[0].key);
  // Switching to a brand that does not have the selected tab lands on the first one.
  const tab = tabs.find((t) => t.key === tabKey) || tabs[0];
  return (
    <Page>
      <PageHeader title="Escalations" />
      <div className="mt-7 grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(280px,360px)_minmax(0,1fr)]">
        <div className={`overflow-hidden ${cardClass}`}>
          {tabs.map((t) => {
            const selected = t.key === tab.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTabKey(t.key)}
                aria-current={selected ? "true" : undefined}
                className={`flex h-[46px] w-full cursor-pointer items-center border-b border-[#F2F4F7] px-4 text-left text-[14px] transition-colors duration-150 last:border-b-0 ${
                  selected
                    ? "bg-[#EFF5FC] font-semibold text-[#1F5596]"
                    : "bg-transparent font-medium text-[#344054] hover:bg-[#F9FAFB]"
                }`}
              >
                <span className="truncate">{t.label}</span>
              </button>
            );
          })}
        </div>
        <div className={`${cardClass} p-6`}>
          {tab.key === "complaints" ? (
            <ComplaintConfigEditor
              key={`complaints-${tenantId}`}
              tenantId={tenantId}
              title={tab.label}
            />
          ) : (
            <>
              {tab.key === "pre_ifr_unhappy" ? (
                <>
                  <PreIfrJourney key={`journey-${tenantId}`} tenantId={tenantId} title={tab.label} />
                  <section data-testid="pre-ifr-settings" className="mt-8 border-t border-[#EAECF0] pt-6">
                    <h3 className="mb-4 text-[15px] font-semibold text-[#101828]">Settings</h3>
                    <div className="[&>div:last-child]:mb-0 [&>div:last-child]:border-0 [&>div:last-child]:pb-0">
                      <PreIfrWorkshop key={`workshop-${tenantId}`} tenantId={tenantId} />
                      <TataIntegration key={`tata-${tenantId}`} tenantId={tenantId} />
                    </div>
                  </section>
                </>
              ) : (
                <SlaEditor key={`${tab.key}-${tenantId}`} tenantId={tenantId} ladder={tab.key} title={tab.label} />
              )}
            </>
          )}
        </div>
      </div>
    </Page>
  );
}

// The Pre-IFR journey: its first message and the escalation steps on one canvas, saved together.
function PreIfrJourney({ tenantId, title }) {
  const firstMessage = useFirstMessage(tenantId);
  return <SlaEditor tenantId={tenantId} ladder="pre_ifr_unhappy" title={title} firstMessage={firstMessage} />;
}

function PageBody({ page, tenant }) {
  const tenantId = tenant.id;
  if (page.key === "journey") {
    return (
      <Page maxWidth={1480}>
        <PageHeader title={page.label} />
        <StageEditor key={`j-${tenantId}`} tenantId={tenantId} />
      </Page>
    );
  }
  if (page.key === "escalations")
    return <EscalationsPage key={tenantId} tenantId={tenantId} />;
  // The brand app's own Roles and access and Organization pages, on this brand. They
  // read the .msd colours, so they sit inside that scope.
  if (page.key === "roles") {
    return (
      <div className="msd bg-transparent px-8 py-6">
        <RolesPage
          key={`r-${tenantId}`}
          tenantId={tenantId}
          canEdit
          canSeePeople
        />
      </div>
    );
  }
  if (page.key === "organization") {
    return (
      <div className="msd bg-transparent px-8 py-6">
        <OrganizationPage
          key={`o-${tenantId}`}
          tenantId={tenantId}
          access={FULL_ACCESS}
        />
      </div>
    );
  }
  if (page.key === "cost") {
    return (
      <Page maxWidth={1100}>
        <PageHeader title={page.label} />
        <CostEditor key={`cost-${tenantId}`} tenantId={tenantId} />
      </Page>
    );
  }
  if (page.key === "features") {
    return (
      <Page maxWidth={1100}>
        <PageHeader title={page.label} />
        <FeatureFlagsEditor key={`ff-${tenantId}`} tenantId={tenantId} />
      </Page>
    );
  }
  if (page.key === "notifications") {
    return (
      <Page maxWidth={1100}>
        <PageHeader title={page.label} />
        <NotificationsEditor key={`pn-${tenantId}`} tenantId={tenantId} />
      </Page>
    );
  }
  if (page.key === "templates") {
    return (
      <Page maxWidth={1100}>
        <PageHeader title={page.label} />
        <Templates
          key={`tp-${tenantId}`}
          tenantId={tenantId}
          brandName={tenant.name}
          brandSlug={tenant.brand_slug}
        />
      </Page>
    );
  }
  if (page.key === "vehicles") {
    return (
      <Page maxWidth={1100}>
        <Vehicles key={`vh-${tenantId}`} tenantId={tenantId} />
      </Page>
    );
  }
  if (page.key === "people") {
    return (
      <Page maxWidth={1100}>
        <People key={`pp-${tenantId}`} tenantId={tenantId} />
      </Page>
    );
  }
  if (page.key === "brands") {
    return (
      <Page maxWidth={860}>
        <PageHeader title={tenant.name} />
        <BrandLogoEditor tenant={tenant} />
        <BrandSidebarImageEditor tenant={tenant} />
        <Chat360Editor tenant={tenant} />
        <Chat360FallbackEditor tenant={tenant} />
      </Page>
    );
  }
  return (
    <Page>
      <PageHeader title={page.label} />
    </Page>
  );
}

export default function SuperAdmin() {
  const { me, signOut } = useAuth();
  const { data, isLoading } = useTenants();
  const [selectedId, setSelectedId] = useState(null);
  const [addingOpen, setAddingOpen] = useState(false);
  const [pageKey, setPageKey] = useState("journey");

  const tenants = useMemo(() => data?.rows || [], [data]);

  useEffect(() => {
    if (!selectedId && tenants.length) setSelectedId(tenants[0].id);
  }, [tenants, selectedId]);

  const selected = tenants.find((t) => t.id === selectedId) || null;
  const page = PAGE_LOOKUP[pageKey];

  return (
    <div className="sa-scope isd flex h-screen min-h-[640px] bg-white text-[14px] text-[#111827]">
      <HoverSidebar
        groups={NAV_GROUPS.map((g) => ({
          label: g.label,
          accent: g.accent,
          items: g.items.map((it) => ({
            key: it.key,
            label: it.label,
            icon: ICONS[it.icon],
            active: pageKey === it.key,
            onClick: () => setPageKey(it.key),
          })),
        }))}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="relative z-30 flex h-[72px] flex-none items-center justify-between gap-4 border-b border-[#EAECF0] bg-white px-8">
          <div className="flex min-w-0 items-center gap-3">
            <BrandSwitcher
              tenants={tenants}
              selected={selected}
              onSelect={setSelectedId}
              onAddBrand={() => setAddingOpen(true)}
              loading={isLoading}
            />
            <span className="flex-none rounded-md bg-[#EFF5FC] px-2 py-[3px] text-[11px] font-semibold text-[#1F5596]">
              Super Admin
            </span>
          </div>

          <AccountMenu
            email={me?.email}
            subtitle="Platform"
            onSignOut={signOut}
          />
        </header>

        <Dialog open={addingOpen} onOpenChange={setAddingOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>New brand</DialogTitle>
              <DialogDescription>
                Creates a new tenant workspace and its Master login.
              </DialogDescription>
            </DialogHeader>
            <NewTenantForm onDone={() => setAddingOpen(false)} />
          </DialogContent>
        </Dialog>

        <main className="min-w-0 flex-1 overflow-y-auto bg-white">
          {page.key === "health" ? (
            // Platform-wide: works with no brand selected, and filters to the
            // selected brand when asked.
            <SystemHealth tenant={selected} />
          ) : isLoading ? (
            <Page>
              <div className="flex items-center gap-2 text-[13px] text-[#6B7280]">
                <Loader2 className="animate-spin" size={15} /> Loading
              </div>
            </Page>
          ) : !selected ? (
            <Page>
              <PageHeader title={page.label} />
            </Page>
          ) : (
            <PageBody page={page} tenant={selected} />
          )}
        </main>
      </div>
    </div>
  );
}
